import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';

import { EMTPY_LIST_ITEMS } from '@common/constants';
import { SidebarPosition } from '@core/types';
import { ThumbnailType } from '@ui/types';
import Thumbnail from '../Thumbnail';
import ThumbnailNoImage from '../ThumbnailNoImage';
import ThumbnailTracked from '../ThumbnailTracked';

interface ThumbnailItem {
	displaySetInstanceUID: string;
	imageSrc?: string;
	imageAltText?: string;
	seriesDate?: string;
	seriesNumber?: string | number;
	numInstances?: number;
	description?: string;
	componentType: ThumbnailType;
	viewportIdentificator?: string[];
	isTracked?: boolean;
	dragData?;
	modality?: string;
	orientation?: string;
	canReject?: boolean;
	onReject?: () => void;
	messages?: any;
	isHydratedForDerivedDisplaySet?: boolean;
	loadingProgress?: number;
	isSubSeries?: boolean;
	isOriginSeries?: boolean;
	SeriesInstanceUID?: string;
}

interface ThumbnailListProps {
	thumbnails: ThumbnailItem[];
	activeDisplaySetInstanceUIDs?: string[];
	onClickThumbnail: (displaySetInstanceUID: string) => void;
	onDoubleClickThumbnail: (displaySetInstanceUID: string) => void;
	onClickUntrack: (displaySetInstanceUID: string) => void;
	servicesManager?: any;
	isTracked?: boolean;
	onSubPanel?: boolean;
	onMobile?: boolean;
	sidePosition?: SidebarPosition;
}

const thumbnailListClasses = {
	default: 'overflow-x-hidden h-full',
	onSubPanel: 'grid grid-cols-2 gap-1',
	onMobile: 'flex w-full flex-row overflow-x-auto',
};

const ThumbnailList: React.FC<ThumbnailListProps> = ({
	thumbnails,
	onClickThumbnail,
	onDoubleClickThumbnail,
	onClickUntrack,
	activeDisplaySetInstanceUIDs = EMTPY_LIST_ITEMS,
	onSubPanel = false,
	onMobile = false,
	sidePosition,
}) => {
	const styles = useMemo(() => {
		if (onMobile) {
			return thumbnailListClasses['onMobile'];
		}
		if (onSubPanel) {
			return thumbnailListClasses['onSubPanel'];
		}
		return thumbnailListClasses['default'];
	}, [onMobile, onSubPanel]);

	const [collapsedThumbnails, setCollapsedThumbnail] = useState(new Map<string, boolean>());

	const toggleCollapse = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		const seriesInstanceUID = e.currentTarget.dataset.seriesInstanceUid;
		const collapsed = e.currentTarget.dataset.collapsed === 'true';
		setCollapsedThumbnail((prev) => new Map(prev).set(seriesInstanceUID, !collapsed));
	}, []);

	return (
		<div id="thumbnail-list" className={classNames('saola-scrollbar mx-auto bg-primary-dark', styles)}>
			{thumbnails.map(
				({
					displaySetInstanceUID,
					description,
					dragData,
					seriesNumber,
					numInstances,
					modality,
					componentType,
					seriesDate,
					orientation,
					viewportIdentificator,
					isTracked,
					canReject,
					onReject,
					imageSrc,
					messages,
					imageAltText,
					isHydratedForDerivedDisplaySet,
					loadingProgress,
					isSubSeries,
					isOriginSeries,
					SeriesInstanceUID,
				}) => {
					const isActive = activeDisplaySetInstanceUIDs.includes(displaySetInstanceUID);
					let collapsed = collapsedThumbnails.get(SeriesInstanceUID);
					if (collapsed === undefined || collapsed === null) {
						collapsed = true; // mặc định collapse
					}
					switch (componentType) {
						case 'thumbnail':
							return (
								<Thumbnail
									key={displaySetInstanceUID}
									displaySetInstanceUID={displaySetInstanceUID}
									dragData={dragData}
									description={description}
									seriesNumber={seriesNumber}
									numInstances={numInstances}
									orientation={orientation}
									imageSrc={imageSrc}
									imageAltText={imageAltText}
									messages={messages}
									viewportIdentificator={viewportIdentificator}
									isActive={isActive}
									onClick={() => onClickThumbnail(displaySetInstanceUID)}
									onDoubleClick={() => onDoubleClickThumbnail(displaySetInstanceUID)}
									loadingProgress={loadingProgress}
									collapsible={isOriginSeries}
									isSubSeries={isSubSeries}
									collapsed={collapsed}
									onToggleCollapse={toggleCollapse}
									seriesInstanceUID={SeriesInstanceUID}
									sidePosition={sidePosition}
								/>
							);
						case 'thumbnailTracked':
							return (
								<ThumbnailTracked
									key={displaySetInstanceUID}
									displaySetInstanceUID={displaySetInstanceUID}
									dragData={dragData}
									description={description}
									seriesNumber={seriesNumber}
									numInstances={numInstances}
									orientation={orientation}
									imageSrc={imageSrc}
									imageAltText={imageAltText}
									messages={messages}
									viewportIdentificator={viewportIdentificator}
									isTracked={isTracked}
									isActive={isActive}
									onClick={() => onClickThumbnail(displaySetInstanceUID)}
									onDoubleClick={() => onDoubleClickThumbnail(displaySetInstanceUID)}
									onClickUntrack={() => onClickUntrack(displaySetInstanceUID)}
									loadingProgress={loadingProgress}
									collapsible={isOriginSeries}
									isSubSeries={isSubSeries}
									collapsed={collapsed}
									onToggleCollapse={toggleCollapse}
									seriesInstanceUID={SeriesInstanceUID}
									sidePosition={sidePosition}
								/>
							);
						case 'thumbnailNoImage':
							return (
								<ThumbnailNoImage
									isActive={isActive}
									key={displaySetInstanceUID}
									displaySetInstanceUID={displaySetInstanceUID}
									dragData={dragData}
									modality={modality}
									modalityTooltip={_getModalityTooltip(modality)}
									messages={messages}
									seriesDate={seriesDate}
									description={description}
									canReject={canReject}
									onReject={onReject}
									onClick={() => onClickThumbnail(displaySetInstanceUID)}
									onDoubleClick={() => onDoubleClickThumbnail(displaySetInstanceUID)}
									viewportIdentificator={viewportIdentificator}
									isHydratedForDerivedDisplaySet={isHydratedForDerivedDisplaySet}
									loadingProgress={loadingProgress}
									sidePosition={sidePosition}
								/>
							);
						default:
							return <></>;
					}
				},
			)}
		</div>
	);
};

// TODO: Support "Viewport Identificator"?
function _getModalityTooltip(modality) {
	if (_modalityTooltips[modality]) {
		return _modalityTooltips[modality];
	}

	return 'Unknown';
}

const _modalityTooltips = {
	SR: 'Structured Report',
	SEG: 'Segmentation',
	RTSTRUCT: 'RT Structure Set',
};

export default ThumbnailList;
